import React, { useEffect, useState } from 'react';
import Header from "../Header";
import Sidebar from "./SideBar";
import Content from '../Content';
import authStorage from '../../utilities/Storage';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  FiUsers, FiCalendar, FiActivity, FiXCircle, FiTrendingUp, FiRefreshCw, 
  FiFilter, FiDownload, FiBarChart2, FiClock, FiCheckCircle, FiAlertCircle,
  FiMessageSquare, FiVideo, FiVideoOff
} from 'react-icons/fi';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ResponsiveContainer } from 'chart.js';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

export default function AdminDashboard() {
  const [email, setEmail] = useState(null);
  const [company_name, setCompany_Name] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    totalMeetings: 0,
    activeMeetings: 0,
    cancelledMeetings: 0,
    completedMeetings: 0,
    totalConversations: 0,
    activeConversations: 0
  });
  const [monthlyConversationData, setMonthlyConversationData] = useState({
    labels: [],
    datasets: []
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('6months'); // '1month', '3months', '6months', '1year'
  const [chartType, setChartType] = useState('line'); // 'line' or 'bar'

  // API base URL
  const API_URL = 'https://backend-livestream-production.up.railway.app/api';

  // Fetch dashboard data from your APIs
  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Get user email from storage
      const userData = authStorage.getUser();
      let userEmail = null;
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          userEmail = user.email;
        } catch (error) {
          console.error("Failed to parse user data:", error);
        }
      }
      
      if (!userEmail) {
        throw new Error('User email not found. Please log in again.');
      }
      
      // Fetch users data
      const usersResponse = await axios.get(`${API_URL}/get-userbyEmail`, {
        params: { email: userEmail }
      });
      
      // Fetch meetings data
      const meetingsResponse = await axios.get(`${API_URL}/meetings`, {
        params: { email: userEmail }
      });
      
      // Fetch messages data for community chat
      const messagesResponse = await axios.get(`${API_URL}/get-messages`, {
        params: {
          email: userEmail,
          chat_id: 'community',
          include_sent: true
        }
      });
      
      // Process the data
      let totalUsers = 0;
      let totalMeetings = 0;
      let activeMeetings = 0;
      let cancelledMeetings = 0;
      let completedMeetings = 0;
      let totalConversations = 0;
      let activeConversations = 0;
      
      // Process users data
      if (usersResponse.data && Array.isArray(usersResponse.data.data)) {
        totalUsers = usersResponse.data.data.length;
        
        // Create user activity data
        const activityData = usersResponse.data.data.slice(0, 5).map(user => {
          const now = new Date();
          const lastActive = user.lastLogin ? new Date(user.lastLogin) : new Date(user.createdAt);
          const diffMs = now - lastActive;
          const diffMins = Math.floor(diffMs / 60000);
          
          let status = 'Offline';
          let lastActiveText = 'Unknown';
          
          if (diffMins < 5) {
            status = 'Active';
            lastActiveText = 'Just now';
          } else if (diffMins < 60) {
            status = 'Active';
            lastActiveText = `${diffMins} min ago`;
          } else if (diffMins < 1440) {
            status = 'Away';
            lastActiveText = `${Math.floor(diffMins / 60)} hours ago`;
          } else if (diffMins < 10080) {
            status = 'Offline';
            lastActiveText = 'Yesterday';
          } else {
            status = 'Offline';
            lastActiveText = lastActive.toLocaleDateString();
          }
          
          const initials = user.full_name ? 
            user.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : 
            user.email.substring(0, 2).toUpperCase();
          
          return {
            id: user.id || user._id,
            name: user.full_name || user.name || 'Unknown User',
            email: user.email,
            status: status,
            lastActive: lastActiveText,
            initials: initials
          };
        });
        
        setUserActivity(activityData);
      }
      
      // Process meetings data
      if (meetingsResponse.data && meetingsResponse.data.success && Array.isArray(meetingsResponse.data.meetings)) {
        totalMeetings = meetingsResponse.data.meetings.length;
        
        meetingsResponse.data.meetings.forEach(meeting => {
          const meetingDateTime = new Date(meeting.created_at);
          const now = new Date();
          
          if (meeting.status === 'ongoing') {
            activeMeetings++;
          } else if (meeting.status === 'cancelled') {
            cancelledMeetings++;
          } else if (meeting.status === 'completed' || meetingDateTime < now) {
            completedMeetings++;
          }
        });
      }
      
      // Process messages data
      if (messagesResponse.data && messagesResponse.data.ok && Array.isArray(messagesResponse.data.data)) {
        totalConversations = messagesResponse.data.data.length;
        
        // Count active conversations (messages from last 24 hours)
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        
        activeConversations = messagesResponse.data.data.filter(msg => 
          new Date(msg.timestamp) > oneDayAgo
        ).length;
        
        // Create monthly conversation data
        const monthlyData = {};
        const now = new Date();
        const monthsToShow = timeRange === '1month' ? 1 : timeRange === '3months' ? 3 : timeRange === '6months' ? 6 : 12;
        
        // Initialize monthly data
        for (let i = 0; i < monthsToShow; i++) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
          monthlyData[monthKey] = 0;
        }
        
        // Count messages by month
        messagesResponse.data.data.forEach(msg => {
          const msgDate = new Date(msg.timestamp);
          const monthKey = msgDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
          
          if (monthlyData.hasOwnProperty(monthKey)) {
            monthlyData[monthKey]++;
          }
        });
        
        // Convert to chart format
        const labels = Object.keys(monthlyData).reverse();
        const counts = Object.values(monthlyData).reverse();
        
        setMonthlyConversationData({
          labels: labels,
          datasets: [
            {
              label: 'Conversation Count',
              data: counts,
              borderColor: 'rgba(99, 102, 241, 1)',
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              borderWidth: 2,
              fill: true,
              tension: 0.4
            }
          ]
        });
        
        // Create recent activity from messages
        const recentMsgs = messagesResponse.data.data
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          .slice(0, 5)
          .map(msg => {
            const msgDate = new Date(msg.timestamp);
            const now = new Date();
            const diffMs = now - msgDate;
            const diffMins = Math.floor(diffMs / 60000);
            
            let timeText = '';
            if (diffMins < 1) {
              timeText = 'Just now';
            } else if (diffMins < 60) {
              timeText = `${diffMins} min ago`;
            } else if (diffMins < 1440) {
              timeText = `${Math.floor(diffMins / 60)} hours ago`;
            } else {
              timeText = msgDate.toLocaleDateString();
            }
            
            let icon = <FiMessageSquare />;
            let iconClass = 'bg-primary bg-opacity-10 text-primary';
            let activityType = 'Message';
            
            if (msg.message.includes('is calling you')) {
              icon = <FiVideo />;
              iconClass = 'bg-success bg-opacity-10 text-success';
              activityType = 'Video Call';
            } else if (msg.message.includes('meeting')) {
              icon = <FiCalendar />;
              iconClass = 'bg-info bg-opacity-10 text-info';
              activityType = 'Meeting';
            }
            
            return {
              id: msg.id,
              type: activityType,
              user: msg.sender_name || 'Unknown User',
              message: msg.message,
              time: timeText,
              icon: icon,
              iconClass: iconClass
            };
          });
        
        setRecentActivity(recentMsgs);
      }
      
      // Update dashboard data
      setDashboardData({
        totalUsers,
        totalMeetings,
        activeMeetings,
        cancelledMeetings,
        completedMeetings,
        totalConversations,
        activeConversations
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  function getUserRegistrationDetails() {
    const userData = authStorage.getUser();
    if (!userData) return;
    
    try {
      const user = JSON.parse(userData);
      setEmail(user.email);
      setCompany_Name(user.company_name);
    } catch (error) {
      console.error("Failed to parse user data:", error);
      setLoading(false);
    }
  }

  useEffect(() => {
    getUserRegistrationDetails();
    fetchDashboardData();
  }, [timeRange]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  const toggleChartType = () => {
    setChartType(chartType === 'line' ? 'bar' : 'line');
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Monthly Conversation Count',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      }
    }
  };

  // Calculate meeting status percentages
  const totalMeetingStatus = dashboardData.completedMeetings + dashboardData.activeMeetings + 
    dashboardData.cancelledMeetings + (dashboardData.totalMeetings - dashboardData.completedMeetings - 
    dashboardData.activeMeetings - dashboardData.cancelledMeetings);
  
  const completedPercentage = totalMeetingStatus > 0 ? 
    Math.round((dashboardData.completedMeetings / totalMeetingStatus) * 100) : 0;
  const activePercentage = totalMeetingStatus > 0 ? 
    Math.round((dashboardData.activeMeetings / totalMeetingStatus) * 100) : 0;
  const cancelledPercentage = totalMeetingStatus > 0 ? 
    Math.round((dashboardData.cancelledMeetings / totalMeetingStatus) * 100) : 0;
  const scheduledPercentage = totalMeetingStatus > 0 ? 
    Math.round(((dashboardData.totalMeetings - dashboardData.completedMeetings - 
    dashboardData.activeMeetings - dashboardData.cancelledMeetings) / totalMeetingStatus) * 100) : 0;

  return (
    <div className="app">
      {/* Mobile Header */}
      <div className="d-md-none mobile-header bg-white shadow-sm p-3 d-flex justify-content-between align-items-center">
        <button 
          className="btn btn-outline-secondary"
          onClick={toggleMobileMenu}
        >
          <i className="bi bi-list"></i>
        </button>
        <h5 className="mb-0 fw-bold">Admin Dashboard</h5>
        <button className="btn btn-outline-secondary" onClick={handleRefresh}>
          {refreshing ? (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <FiRefreshCw />
          )}
        </button>
      </div>

      {/* Sidebar with mobile toggle */}
      <div className={`sidebar-wrapper ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <Sidebar />
      </div>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={toggleMobileMenu}
        ></div>
      )}

      <div className="main-content">
        {/* Regular Header (hidden on mobile) */}
        <div className="d-none d-md-block">
          <Header />
        </div>

        <Content>
          <motion.div 
            className="container-fluid py-4"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {/* Dashboard Header */}
            <motion.div variants={itemVariants} className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className="fw-bold mb-1">Admin Dashboard</h2>
                  <p className="text-muted mb-0">Welcome back! Here's what's happening with your platform today.</p>
                </div>
                <div className="d-flex gap-2">
                  <div className="btn-group" role="group">
                    <button 
                      type="button" 
                      className={`btn ${timeRange === '1month' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleTimeRangeChange('1month')}
                    >
                      1M
                    </button>
                    <button 
                      type="button" 
                      className={`btn ${timeRange === '3months' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleTimeRangeChange('3months')}
                    >
                      3M
                    </button>
                    <button 
                      type="button" 
                      className={`btn ${timeRange === '6months' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleTimeRangeChange('6months')}
                    >
                      6M
                    </button>
                    <button 
                      type="button" 
                      className={`btn ${timeRange === '1year' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleTimeRangeChange('1year')}
                    >
                      1Y
                    </button>
                  </div>
                  <button className="btn btn-outline-primary" onClick={handleRefresh}>
                    {refreshing ? (
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      <FiRefreshCw />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Error Alert */}
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
              )}
            </motion.div>

            {/* Stats Cards */}
            <motion.div variants={itemVariants} className="row mb-4">
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-primary me-3">
                        <FiUsers />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Total Users</h6>
                        <h3 className="fw-bold mb-0">{dashboardData.totalUsers.toLocaleString()}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-success bg-opacity-10 text-success">
                        <FiTrendingUp className="me-1" />12%
                      </span>
                      <span className="text-muted ms-2 small">vs last month</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-info me-3">
                        <FiCalendar />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Total Meetings</h6>
                        <h3 className="fw-bold mb-0">{dashboardData.totalMeetings.toLocaleString()}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-success bg-opacity-10 text-success">
                        <FiTrendingUp className="me-1" />8%
                      </span>
                      <span className="text-muted ms-2 small">vs last month</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-success me-3">
                        <FiActivity />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Active Conversations</h6>
                        <h3 className="fw-bold mb-0">{dashboardData.activeConversations.toLocaleString()}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-success bg-opacity-10 text-success">
                        <FiCheckCircle className="me-1" />Live
                      </span>
                      <span className="text-muted ms-2 small">Last 24 hours</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-danger me-3">
                        <FiXCircle />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Cancelled Meetings</h6>
                        <h3 className="fw-bold mb-0">{dashboardData.cancelledMeetings.toLocaleString()}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-danger bg-opacity-10 text-danger">
                        <FiAlertCircle className="me-1" />{cancelledPercentage}%
                      </span>
                      <span className="text-muted ms-2 small">cancellation rate</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Conversation Chart */}
            <motion.div variants={itemVariants} className="row mb-4">
              <div className="col-lg-8 mb-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold">Monthly Conversation Count</h5>
                    <div className="d-flex gap-2">
                      <button 
                        className={`btn btn-sm ${chartType === 'line' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={toggleChartType}
                      >
                        <FiBarChart2 />
                      </button>
                      <button className="btn btn-sm btn-outline-secondary">
                        <FiDownload />
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div style={{ height: '300px' }}>
                      {chartType === 'line' ? (
                        <Line options={chartOptions} data={monthlyConversationData} />
                      ) : (
                        <Bar options={chartOptions} data={monthlyConversationData} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Recent Activity */}
              <div className="col-lg-4 mb-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-0 py-3">
                    <h5 className="mb-0 fw-bold">Recent Activity</h5>
                  </div>
                  <div className="card-body">
                    <div className="activity-list">
                      {recentActivity.length > 0 ? (
                        recentActivity.map((activity) => (
                          <div key={activity.id} className="activity-item d-flex align-items-start mb-3">
                            <div className={`activity-icon ${activity.iconClass} me-3`}>
                              {activity.icon}
                            </div>
                            <div className="flex-grow-1">
                              <p className="mb-1 fw-medium">{activity.type}</p>
                              <p className="text-muted small mb-0">{activity.message}</p>
                              <p className="text-muted small mb-0">{activity.time}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted">No recent activity</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Additional Stats Row */}
            <motion.div variants={itemVariants} className="row">
              <div className="col-lg-6 mb-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-0 py-3">
                    <h5 className="mb-0 fw-bold">Meeting Status Distribution</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-6 mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <div className="status-indicator bg-success me-2"></div>
                          <span className="fw-medium">Completed</span>
                        </div>
                        <div className="progress mb-2" style={{ height: '8px' }}>
                          <div className="progress-bar bg-success" role="progressbar" style={{ width: `${completedPercentage}%` }} aria-valuenow={completedPercentage} aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                        <span className="text-muted small">{completedPercentage}% of all meetings ({dashboardData.completedMeetings})</span>
                      </div>
                      
                      <div className="col-6 mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <div className="status-indicator bg-info me-2"></div>
                          <span className="fw-medium">Scheduled</span>
                        </div>
                        <div className="progress mb-2" style={{ height: '8px' }}>
                          <div className="progress-bar bg-info" role="progressbar" style={{ width: `${scheduledPercentage}%` }} aria-valuenow={scheduledPercentage} aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                        <span className="text-muted small">{scheduledPercentage}% of all meetings</span>
                      </div>
                      
                      <div className="col-6">
                        <div className="d-flex align-items-center mb-2">
                          <div className="status-indicator bg-warning me-2"></div>
                          <span className="fw-medium">In Progress</span>
                        </div>
                        <div className="progress mb-2" style={{ height: '8px' }}>
                          <div className="progress-bar bg-warning" role="progressbar" style={{ width: `${activePercentage}%` }} aria-valuenow={activePercentage} aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                        <span className="text-muted small">{activePercentage}% of all meetings ({dashboardData.activeMeetings})</span>
                      </div>
                      
                      <div className="col-6">
                        <div className="d-flex align-items-center mb-2">
                          <div className="status-indicator bg-danger me-2"></div>
                          <span className="fw-medium">Cancelled</span>
                        </div>
                        <div className="progress mb-2" style={{ height: '8px' }}>
                          <div className="progress-bar bg-danger" role="progressbar" style={{ width: `${cancelledPercentage}%` }} aria-valuenow={cancelledPercentage} aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                        <span className="text-muted small">{cancelledPercentage}% of all meetings ({dashboardData.cancelledMeetings})</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-6 mb-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-0 py-3">
                    <h5 className="mb-0 fw-bold">User Activity</h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-borderless">
                        <thead>
                          <tr>
                            <th scope="col">User</th>
                            <th scope="col">Status</th>
                            <th scope="col">Last Active</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userActivity.length > 0 ? (
                            userActivity.map((user) => (
                              <tr key={user.id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="user-avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
                                      {user.initials}
                                    </div>
                                    <span>{user.name}</span>
                                  </div>
                                </td>
                                <td>
                                  {user.status === 'Active' && <span className="badge bg-success bg-opacity-10 text-success">Active</span>}
                                  {user.status === 'Away' && <span className="badge bg-warning bg-opacity-10 text-warning">Away</span>}
                                  {user.status === 'Offline' && <span className="badge bg-secondary bg-opacity-10 text-secondary">Offline</span>}
                                </td>
                                <td>{user.lastActive}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" className="text-center">
                                <p className="text-muted">No user activity data available</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </Content>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="d-md-none mobile-bottom-nav fixed-bottom bg-white shadow-lg">
        <div className="d-flex justify-content-around">
          <button className="btn btn-link text-dark">
            <i className="bi bi-house-door-fill fs-5"></i>
            <small className="d-block">Dashboard</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-calendar-event fs-5"></i>
            <small className="d-block">Meetings</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-people fs-5"></i>
            <small className="d-block">Users</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-gear fs-5"></i>
            <small className="d-block">Settings</small>
          </button>
        </div>
      </div>

      <style jsx>{`
        .app {
          display: flex;
          min-height: 100vh;
          position: relative;
        }
        
        .sidebar-wrapper {
          width: 250px;
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }
        
        .main-content {
          flex-grow: 1;
          padding-bottom: 60px; /* Space for mobile bottom nav */
        }
        
        .mobile-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
        }
        
        .mobile-bottom-nav {
          height: 60px;
          z-index: 1000;
        }
        
        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 999;
        }
        
        @media (max-width: 767.98px) {
          .sidebar-wrapper {
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            z-index: 1000;
            transform: translateX(-100%);
          }
          
          .sidebar-wrapper.mobile-open {
            transform: translateX(0);
          }
          
          .main-content {
            margin-left: 0;
          }
        }
        
        /* Stats Card Styles */
        .stats-card {
          transition: all 0.3s ease;
        }
        
        .stats-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
        }
        
        .stats-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }
        
        .stats-icon-primary {
          background: rgba(99, 102, 241, 0.1);
          color: #6366f1;
        }
        
        .stats-icon-info {
          background: rgba(23, 162, 184, 0.1);
          color: #17a2b8;
        }
        
        .stats-icon-success {
          background: rgba(40, 167, 69, 0.1);
          color: #28a745;
        }
        
        .stats-icon-danger {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        }
        
        /* Activity List Styles */
        .activity-list {
          max-height: 400px;
          overflow-y: auto;
        }
        
        .activity-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        /* Status Indicator */
        .status-indicator {
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
      `}</style>
    </div>
  )
}